import { clamp } from "apparatus/library/common";
import { Deselect } from "command/basic";
import { clog } from "log";
import store from "store/store";
import Flags from "flags";
/*
 * Functions and helpers for exporting current canvas
 * into various image formats.
 */
/** Maximum zoom level to use when exporting (retina). */
const MAX_RENDER_ZOOM = 2.0;
/**
 * Converts a data URL into a Blob object.
 */
export function dataUrlToBlob(dataUrl) {
    const parts = dataUrl.split(",");
    const byteString = atob(parts[1]);
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mime });
}
let previousBlobUrls = [];
/**
 * Revokes any previously generated object URLs to free memory.
 */
export function revokePreviousBlobUrls() {
    for (const url of previousBlobUrls) {
        if (url && url.startsWith("blob:") && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
            try {
                URL.revokeObjectURL(url);
            }
            catch (e) {
                // Ignore any error during revocation
            }
        }
    }
    previousBlobUrls = [];
}
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("beforeunload", () => {
        revokePreviousBlobUrls();
    });
}
const THUMBNAIL_WIDTH = 450;
const THUMBNAIL_HEIGHT = 300;
const THUMBNAIL_PADDING = 15;
export function exportFromCanvasJPGThumbnail(project, layers, bounds) {
    // Deselect any selected items. Do this first as this
    // will trigger a React update, setting the zoom.
    let deselectCommand = new Deselect(store.selectedObjectsID());
    deselectCommand.execute();
    // Store current view properties.
    let currentViewSize = project.view.viewSize;
    let currentCenter = project.view.center;
    let currentZoom = project.view.zoom;
    // Calculate temporary render zoom, depending on the amount of
    // space the diagram takes and what's allowed.
    let { scale, x, y } = getExportZoomForThumbnail(bounds);
    // Shift the view center to align with center of the items.
    project.view.center = bounds.center;
    // Temporarily set zoom. If the export does not stay at this zoom, it means
    // some other update is triggering setZoom back to the correct value during
    // the export (this method is called on a background thread.)
    project.view.zoom = scale;
    // Scale down to (280, 180) using values from getExportZoomForThumbnail.
    let scaledBounds = bounds.scale(x, y);
    // Expand bounds just to be sure.
    scaledBounds = scaledBounds.expand(THUMBNAIL_PADDING);
    // Shift the items so that they are moved to the top left corner
    // as opposed to centered in the view. This is important for next step.
    // Intuition: currentViewSize is zoom-independent size of the canvas. We are trying
    // to find how much space the scaled image takes up on the canvas, so we can
    // calculate the gap we need to remove. viewCenter is zoom-dependent and so
    // we need to scale by exportZoom to get the right amount of shift.
    let shift = new paper.Point(currentViewSize.subtract(scaledBounds.size).divide(2 * scale));
    project.view.center = project.view.center.add(shift);
    // Restrict render area to the scaled bounds. Note that the top-left corner stays at 0,0
    project.view.viewSize = scaledBounds.size;
    // Add a white rectangle to the backing layer before exporting JPG.
    // Expand the regular bounds by about 30 which should cover most small
    // zooms.
    let backingRectangle = scaledBounds.scale(1 / scale).expand(30).toShape();
    backingRectangle.fillColor = "white";
    layers.exportOnlyBacking.addChild(backingRectangle);
    // Hide the render area.
    layers.overlay.visible = false;
    // Trigger render then export to JPG.
    project.view.draw();
    let jpgData = project.view.element.toDataURL("image/jpeg", 0.8);
    // Revert.
    layers.exportOnlyBacking.removeChildren();
    project.view.viewSize = currentViewSize;
    project.view.center = currentCenter;
    project.view.zoom = currentZoom;
    deselectCommand.undo();
    layers.overlay.visible = true;
    return jpgData;
}
/**
 * Watermark configuration constants.
 * Sizing and padding are expressed in output pixels for easy tuning.
 */
export const WATERMARK_IMAGE_SRC = "./images/logo_text_black.png";
export const WATERMARK_NATURAL_WIDTH = 476;
export const WATERMARK_NATURAL_HEIGHT = 102;
export const WATERMARK_MIN_HEIGHT_PX = 16;
export const WATERMARK_MAX_HEIGHT_PX = 28;
export const WATERMARK_HEIGHT_RATIO = 0.08; // 8% of exported diagram height
export const WATERMARK_MAX_WIDTH_RATIO = 0.28; // Maximum 28% of exported diagram width
export const WATERMARK_PADDING_PX = 12; // Padding from bottom-right corner in px
export const WATERMARK_OPACITY = 0.85; // Opacity of the watermark logo
let preloadedWatermarkImg;
let watermarkCanvas = null;
let watermarkPromise = null;
export function preloadWatermarkImage(src = WATERMARK_IMAGE_SRC) {
    if (typeof Image === "undefined")
        return undefined;
    if (!preloadedWatermarkImg) {
        preloadedWatermarkImg = new Image();
        preloadedWatermarkImg.src = src;
    }
    return preloadedWatermarkImg;
}
export function ensureWatermarkLoaded(src = WATERMARK_IMAGE_SRC) {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.resolve(null);
    }
    if (watermarkCanvas) {
        return Promise.resolve(watermarkCanvas);
    }
    if (watermarkPromise) {
        return watermarkPromise;
    }
    watermarkPromise = new Promise((resolve) => {
        const img = preloadWatermarkImage(src) || new Image();
        const onLoaded = () => {
            try {
                const canvas = document.createElement("canvas");
                const w = img.naturalWidth || img.width || WATERMARK_NATURAL_WIDTH;
                const h = img.naturalHeight || img.height || WATERMARK_NATURAL_HEIGHT;
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.drawImage(img, 0, 0, w, h);
                    watermarkCanvas = canvas;
                    resolve(canvas);
                    return;
                }
            }
            catch (e) {
                // Ignore canvas errors
            }
            resolve(null);
        };
        if (img.complete && (img.naturalWidth || img.width)) {
            onLoaded();
        }
        else {
            img.onload = onLoaded;
            img.onerror = () => resolve(null);
            if (!img.src) {
                img.src = src;
            }
        }
    });
    return watermarkPromise;
}
function getWatermarkCanvas() {
    if (watermarkCanvas)
        return watermarkCanvas;
    const img = preloadWatermarkImage();
    if (img && img.complete && (img.naturalWidth || img.width) && typeof document !== "undefined") {
        try {
            const canvas = document.createElement("canvas");
            const w = img.naturalWidth || img.width || WATERMARK_NATURAL_WIDTH;
            const h = img.naturalHeight || img.height || WATERMARK_NATURAL_HEIGHT;
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(img, 0, 0, w, h);
                watermarkCanvas = canvas;
                return canvas;
            }
        }
        catch (e) {
            // ignore
        }
    }
    return null;
}
function addWatermarkToLayer(watermarkLayer, project, zoom) {
    watermarkLayer.activate();
    watermarkLayer.bringToFront();
    const canvas = getWatermarkCanvas();
    const img = preloadWatermarkImage();
    const source = canvas || (img && img.complete && (img.naturalWidth || img.width) ? img : (canvas || img || WATERMARK_IMAGE_SRC));
    const raster = new paper.Raster(source);
    watermarkLayer.addChild(raster);
    raster.opacity = WATERMARK_OPACITY;
    const rasterWidth = raster.width || WATERMARK_NATURAL_WIDTH;
    const rasterHeight = raster.height || WATERMARK_NATURAL_HEIGHT;
    const viewWidth = project.view.bounds.width;
    const viewHeight = project.view.bounds.height;
    const imagePixelHeight = viewHeight * zoom;
    const imagePixelWidth = viewWidth * zoom;
    const targetHeightInPixels = clamp(imagePixelHeight * WATERMARK_HEIGHT_RATIO, WATERMARK_MIN_HEIGHT_PX, WATERMARK_MAX_HEIGHT_PX);
    const maxWatermarkWidthInPixels = imagePixelWidth * WATERMARK_MAX_WIDTH_RATIO;
    const scaleByHeight = targetHeightInPixels / rasterHeight;
    const scale = Math.min(scaleByHeight, maxWatermarkWidthInPixels / rasterWidth) / zoom;
    raster.scale(scale);
    const padding = WATERMARK_PADDING_PX / zoom;
    const right = project.view.bounds.right - padding;
    const bottom = project.view.bounds.bottom - padding;
    raster.position = new paper.Point(right - raster.bounds.width / 2, bottom - raster.bounds.height / 2);
    return raster;
}
/**
 * Exports the current canvas contents to multiple image formats.
 *
 * This function is very delicate and assumes that
 * no other processing/editing occurs at the same time.
 * It relies on the data store, the current canvas state
 * and Paper.js operations to prepare for export.
 *
 * It will make destructive edits to the canvas itself,
 * which it then attempts to revert at the end.
 *
 * @param project Paper project to export from
 * @param layers Layers of the current project
 * @param bounds Strict bounds (no padding), in paper coordinates
 *               of the area in canvas to be exported.
 * @param maxPixels Maximum number of pixels to render.
 * @param options Additional export options (e.g. watermark).
 */
export function exportFromCanvas(project, layers, bounds, maxPixels, options) {
    /*
        NOTE: This used to do deselection but this has been moved to shared_modals.tsx::saveImage
        Deselection uses redux so doesn't update until next render cycle.

        Could probably use a promise and return Promise<ImageExportOutput> instead but feeling lazy.
    */
    // Store current view properties.
    let currentViewSize = project.view.viewSize;
    let currentCenter = project.view.center;
    let currentZoom = project.view.zoom;
    // Calculate temporary render zoom, depending on the amount of
    // space the diagram takes and what's allowed.
    let exportZoom = getExportZoom(bounds, maxPixels, project);
    clog(`Rendering image at zoom level ${exportZoom}`);
    // Shift the view center to align with center of the items.
    project.view.center = bounds.center;
    // Temporarily set zoom. If the export does not stay at this zoom, it means
    // some other update is triggering setZoom back to the correct value during
    // the export (this method is called on a background thread.)
    project.view.zoom = exportZoom;
    // Scale the bounds to export zoom, around the view center.
    // This only works because we have adjusted the view center
    // to the bounds' center, avoiding ugly maths.
    let scaledBounds = bounds.scale(exportZoom);
    // Expand bounds just to be sure.
    scaledBounds = scaledBounds.expand(5.0);
    // Shift the items so that they are moved to the top left corner
    // as opposed to centered in the view. This is important for next step.
    // Intuition: currentViewSize is zoom-independent size of the canvas. We are trying
    // to find how much space the scaled image takes up on the canvas, so we can
    // calculate the gap we need to remove. viewCenter is zoom-dependent and so
    // we need to scale by exportZoom to get the right amount of shift.
    let shift = new paper.Point(currentViewSize.subtract(scaledBounds.size).divide(2 * exportZoom));
    project.view.center = project.view.center.add(shift);
    // Restrict render area to the scaled bounds. Note that the top-left corner stays at 0,0
    project.view.viewSize = scaledBounds.size;
    // Hide the export rectangle.
    layers.overlay.visible = false;
    let svgElement;
    let pngData;
    let jpgData;
    try {
        if (Flags.isExportWatermarkEnabled && (options === null || options === void 0 ? void 0 : options.watermark)) {
            addWatermarkToLayer(layers.watermark, project, exportZoom);
        }
        // Export SVG.
        svgElement = project.exportSVG();
        svgElement.setAttribute("viewBox", `0 0 ${scaledBounds.width} ${scaledBounds.height}`);
        // Trigger render then export to PNG.
        project.view.draw();
        pngData = project.view.element.toDataURL("image/png");
        // Add a white rectangle to the backing layer before exporting JPG.
        // Expand the regular bounds by about 30 which should cover most small
        // zooms.
        let backingRectangle = bounds.expand(30).toShape();
        backingRectangle.fillColor = "white";
        layers.exportOnlyBacking.addChild(backingRectangle);
        // Trigger render then export to JPG.
        project.view.draw();
        jpgData = project.view.element.toDataURL("image/jpeg", 0.8);
    }
    finally {
        // Revert.
        layers.watermark.removeChildren();
        layers.apparatus.activate();
        layers.exportOnlyBacking.removeChildren();
        project.view.viewSize = currentViewSize;
        project.view.center = currentCenter;
        project.view.zoom = currentZoom;
        layers.overlay.visible = true;
    }
    revokePreviousBlobUrls();
    // Convert SVG element to data url and blob.
    var seralizer = new XMLSerializer();
    var svgString = seralizer.serializeToString(svgElement);
    // btoa can sometimes fail, see
    // https://stackoverflow.com/questions/23223718/failed-to-execute-btoa-on-window-the-string-to-be-encoded-contains-characte
    let svgDataUrl = "data:image/svg;base64," + btoa(unescape(encodeURIComponent(svgString)));
    let svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    let pngBlob = dataUrlToBlob(pngData);
    let jpgBlob = dataUrlToBlob(jpgData);
    let svgBlobUrl = typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(svgBlob) : svgDataUrl;
    let pngBlobUrl = typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(pngBlob) : pngData;
    let jpgBlobUrl = typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(jpgBlob) : jpgData;
    previousBlobUrls = [svgBlobUrl, pngBlobUrl, jpgBlobUrl];
    return {
        svgElement: svgElement,
        svgDataUrl: svgDataUrl,
        pngDataUrl: pngData,
        jpgDataUrl: jpgData,
        svgBlobUrl: svgBlobUrl,
        pngBlobUrl: pngBlobUrl,
        jpgBlobUrl: jpgBlobUrl,
    };
}
/**
 * Computes the correct zoom for given bounds. This ensures that PNGs and JPGs
 * are not overly huge. This also adjusts for display density.
 */
function getExportZoom(bounds, maxPixels, project) {
    var _a, _b;
    let pixelRatio = (_b = (_a = project === null || project === void 0 ? void 0 : project.view) === null || _a === void 0 ? void 0 : _a.pixelRatio) !== null && _b !== void 0 ? _b : (paper.project ? paper.project.view.pixelRatio : 1);
    let pixelRatioSquared = pixelRatio * pixelRatio;
    let currentSize = Math.max(bounds.width * bounds.height * pixelRatioSquared, 10000);
    let rawZoom = Math.sqrt(maxPixels / currentSize);
    let zoom = Math.min(rawZoom, MAX_RENDER_ZOOM * 2 / pixelRatio);
    zoom = clamp(zoom, 0.01, 10);
    return zoom;
}
/** Compute zoom for scaling down for a thumbnail. */
function getExportZoomForThumbnail(bounds) {
    let pixelRatio = paper.project.view.pixelRatio;
    let scaleY = (THUMBNAIL_HEIGHT - THUMBNAIL_PADDING * 2) / bounds.height / pixelRatio;
    let scaleX = (THUMBNAIL_WIDTH - THUMBNAIL_PADDING * 2) / bounds.width / pixelRatio;
    let scale = Math.min(scaleY, scaleX);
    scale = clamp(scale, 0.01, 10);
    return { scale: scale, x: scaleX, y: scaleY };
}
